import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import { Link } from 'react-router-dom';
import { Calculator, ArrowRight } from 'lucide-react';
import { getCurrentMonthYear } from '../utils/currentDate';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import { formatNumber } from '../utils/timeCalculations';

const CalculoHorasExtraordinariasPage = () => {
  const [salario, setSalario] = useState('');
  const [jornadaSemanal, setJornadaSemanal] = useState('220'); // 44 horas
  const [horasExtras, setHorasExtras] = useState('');
  const [percentual, setPercentual] = useState('50');

  const resultado = useMemo(() => {
    const sal = parseFloat(salario);
    const divisor = parseFloat(jornadaSemanal);
    const horas = parseFloat(horasExtras);
    const perc = parseFloat(percentual);

    if (!sal || !horas || sal <= 0 || horas <= 0) {
      return null;
    }

    const horaNormal = sal / divisor;
    const fator = 1 + (perc / 100);
    const horaExtra = horaNormal * fator;
    const total = horaExtra * horas;

    return {
      horaNormal: horaNormal.toFixed(2),
      horaExtra: horaExtra.toFixed(2),
      total: total.toFixed(2),
      percentual: perc,
      quantidade: horas
    };
  }, [salario, jornadaSemanal, horasExtras, percentual]);

  const faqs = [
    {
      question: 'Como calcular hora extra de 50%?',
      answer: 'Divida o salário mensal pelo divisor da jornada (220 para 44h semanais). O resultado é o valor da hora normal. Multiplique por 1,50 para obter o valor da hora extra de 50%. Depois, multiplique pela quantidade de horas extras trabalhadas.'
    },
    {
      question: 'Quantas horas extras posso fazer por dia?',
      answer: 'A CLT permite no máximo 2 horas extras por dia, conforme o artigo 59. Nas exceções do artigo 61 (serviços inadiáveis ou força maior) esse limite pode ser ultrapassado, mas a regra geral é de 2 horas diárias.'
    },
    {
      question: 'Hora extra de domingo é 100%?',
      answer: 'Sim. As horas trabalhadas em domingos e feriados devem ser remuneradas com acréscimo de 100% sobre o valor da hora normal, salvo quando houver folga compensatória prevista em acordo ou convenção coletiva.'
    },
    {
      question: 'Hora extra entra no cálculo de férias e 13º?',
      answer: 'Sim. As horas extras habituais integram o cálculo de férias, 13º salário, FGTS, INSS e aviso prévio. A média das horas extras realizadas nos últimos 12 meses é considerada para esses cálculos.'
    },
    {
      question: 'Qual o divisor correto para calcular hora extra?',
      answer: 'O divisor depende da jornada semanal: 220 para quem trabalha 44 horas por semana (8h/dia + 4h no sábado), 180 para jornadas de 36 horas semanais, e 150 para 30 horas semanais.'
    },
    {
      question: 'O empregador pode obrigar a fazer hora extra?',
      answer: 'De modo geral, não. A hora extra deve ser acordada entre empregado e empregador. A exceção prevista no artigo 61 da CLT é em caso de força maior ou para atender necessidades inadiáveis.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Cálculo de Horas Extraordinárias 2026 — Guia e Calculadora</title>
        <meta name="description" content="Aprenda como fazer o cálculo de horas extraordinárias corretamente. Use a calculadora grátis para hora extra 50%, 100% e noturna. Passo a passo com exemplos." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
        <link rel="canonical" href="https://calculadoradehoras.net.br/calculo-de-horas-extraordinarias" />
        
        <meta property="og:title" content="Cálculo de Horas Extraordinárias 2026 — Guia e Calculadora" />
        <meta property="og:description" content="Aprenda como fazer o cálculo de horas extraordinárias corretamente. Use a calculadora grátis para hora extra 50%, 100% e noturna." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/calculo-de-horas-extraordinarias" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Cálculo de Horas Extraordinárias 2026 — Guia e Calculadora" />
        <meta name="twitter:description" content="Aprenda como fazer o cálculo de horas extraordinárias corretamente. Use a calculadora grátis para hora extra 50%, 100% e noturna." />
        
        <link rel="alternate" hrefLang="pt-BR" href="https://calculadoradehoras.net.br/calculo-de-horas-extraordinarias" />
        <link rel="alternate" hrefLang="x-default" href="https://calculadoradehoras.net.br/calculo-de-horas-extraordinarias" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Horas Extraordinárias","item":"https://calculadoradehoras.net.br/calculo-de-horas-extraordinarias"}
        ]
      }} />

      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4" aria-label="Breadcrumb">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Horas Extraordinárias</span>
        </nav>
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-dark-text mb-4">
            Calculadora de Horas Extraordinárias
          </h1>
          <p className="text-lg text-body-text">
            Calcule o valor das suas horas extras de forma rápida e precisa
          </p>
          <p className="text-sm text-body-text mt-2" data-testid="last-updated" suppressHydrationWarning>{`Última atualização: ${getCurrentMonthYear()}`}</p>
        </div>

        {/* Simple Calculator */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Salário Bruto Mensal (R$)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={salario}
                onChange={(e) => setSalario(e.target.value)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-lg focus:outline-none focus:border-blue-primary"
                placeholder="3000.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Jornada Semanal
              </label>
              <select
                value={jornadaSemanal}
                onChange={(e) => setJornadaSemanal(e.target.value)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg focus:outline-none focus:border-blue-primary"
              >
                <option value="220">44 horas (÷ 220)</option>
                <option value="180">36 horas (÷ 180)</option>
                <option value="150">30 horas (÷ 150)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Quantidade de Horas Extras
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={horasExtras}
                onChange={(e) => setHorasExtras(e.target.value)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-lg focus:outline-none focus:border-blue-primary"
                placeholder="10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Percentual de Acréscimo
              </label>
              <select
                value={percentual}
                onChange={(e) => setPercentual(e.target.value)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg focus:outline-none focus:border-blue-primary"
              >
                <option value="50">50% — Dias úteis</option>
                <option value="75">75% — Convenção coletiva</option>
                <option value="100">100% — Domingos e feriados</option>
              </select>
            </div>
          </div>

          {resultado && (
            <div className="mt-8 bg-blue-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-dark-text mb-4">Resultado</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-body-text mb-1">Valor da Hora Normal</p>
                  <p className="text-2xl font-bold text-dark-text">R$ {formatNumber(resultado.horaNormal)}</p>
                </div>
                <div>
                  <p className="text-sm text-body-text mb-1">Valor da Hora Extra</p>
                  <p className="text-2xl font-bold text-dark-text">R$ {formatNumber(resultado.horaExtra)}</p>
                </div>
                <div>
                  <p className="text-sm text-body-text mb-1">Percentual Aplicado</p>
                  <p className="text-2xl font-bold text-dark-text">{resultado.percentual}%</p>
                </div>
                <div>
                  <p className="text-sm text-body-text mb-1">Quantidade de Horas</p>
                  <p className="text-2xl font-bold text-dark-text">{resultado.quantidade}</p>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-border-gray">
                <p className="text-sm text-body-text mb-2">Valor Total das Horas Extraordinárias</p>
                <p className="text-4xl font-bold text-blue-primary">R$ {formatNumber(resultado.total)}</p>
              </div>
            </div>
          )}
        </div>

        {/* CTA to Calculator Page */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 md:p-8 mb-8 border-2 border-blue-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Calculator className="text-blue-primary" size={32} />
                <p className="font-bold text-xl font-semibold text-dark-text">
                  Precisa de mais recursos?
                </p>
              </div>
              <p className="text-base text-body-text">
                Use nossa <strong>calculadora de horas extras completa</strong> para calcular rapidamente com mais opções e funcionalidades avançadas.
              </p>
            </div>
            <Link
              to="/calculadora-de-horas-extras"
              className="flex items-center gap-2 bg-blue-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
            >
              Usar Calculadora Completa
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>

        <AdSlot />

        {/* Content Sections */}
        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            O Que São Horas Extraordinárias?
          </h2>
          <p className="text-base text-body-text mb-4">
            Horas extraordinárias são as horas trabalhadas além da jornada regular de trabalho definida no contrato ou na legislação. No Brasil, a <strong>Consolidação das Leis do Trabalho (CLT)</strong> estabelece, no artigo 58, que a jornada padrão é de 8 horas diárias e 44 horas semanais.
          </p>
          <p className="text-base text-body-text mb-4">
            Toda hora que ultrapassar esse limite é considerada hora extraordinária e deve ser remunerada com um acréscimo mínimo sobre o valor da hora normal. Esse acréscimo varia conforme o dia e as condições de trabalho.
          </p>
          <p className="text-base text-body-text">
            O <strong>cálculo de horas extraordinárias</strong> é um dos temas que mais geram dúvidas entre trabalhadores e empregadores. Entender as regras e saber calcular corretamente é fundamental para garantir seus direitos e evitar problemas trabalhistas.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Percentuais de Acréscimo por Tipo de Hora Extra
          </h2>
          <p className="text-base text-body-text mb-6">
            A legislação brasileira prevê diferentes percentuais de acréscimo dependendo do dia e da situação em que a hora extra é prestada. Veja a tabela com os valores atualizados.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="border border-border-gray px-4 py-3 text-left font-semibold text-dark-text">Situação</th>
                  <th className="border border-border-gray px-4 py-3 text-left font-semibold text-dark-text">Acréscimo</th>
                  <th className="border border-border-gray px-4 py-3 text-left font-semibold text-dark-text">Base Legal</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-border-gray px-4 py-3">Dias úteis (segunda a sábado)</td>
                  <td className="border border-border-gray px-4 py-3"><strong>50%</strong></td>
                  <td className="border border-border-gray px-4 py-3">Art. 7º, XVI da CF</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-border-gray px-4 py-3">Convenção coletiva (categorias específicas)</td>
                  <td className="border border-border-gray px-4 py-3"><strong>75%</strong></td>
                  <td className="border border-border-gray px-4 py-3">Acordo coletivo</td>
                </tr>
                <tr>
                  <td className="border border-border-gray px-4 py-3">Domingos e feriados</td>
                  <td className="border border-border-gray px-4 py-3"><strong>100%</strong></td>
                  <td className="border border-border-gray px-4 py-3">Lei 605/1949 e Súmula 146 do TST</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-border-gray px-4 py-3">Hora extra noturna (22h às 5h)</td>
                  <td className="border border-border-gray px-4 py-3"><strong>50% + 20%</strong></td>
                  <td className="border border-border-gray px-4 py-3">Art. 73 da CLT</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-base text-body-text mt-4">
            <strong>Dica importante:</strong> Muitas convenções coletivas estabelecem percentuais maiores que o mínimo legal. Antes de calcular, consulte a convenção da sua categoria para saber o percentual correto.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Como Calcular Hora Extra — Passo a Passo
          </h2>
          <p className="text-base text-body-text mb-6">
            O cálculo de horas extraordinárias segue uma lógica simples em três etapas. Veja como chegar ao valor exato que você tem a receber.
          </p>

          <div className="space-y-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-dark-text mb-2">1. Descubra o valor da hora normal</h3>
              <p className="text-base text-body-text">
                Divida o salário bruto mensal pelo divisor da jornada. Para 44 horas semanais, o divisor é 220. Para 36 horas, use 180. Para 30 horas, use 150. A fórmula é: <strong>Salário ÷ Divisor = Valor da hora normal</strong>.
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-dark-text mb-2">2. Aplique o percentual de acréscimo</h3>
              <p className="text-base text-body-text">
                Multiplique o valor da hora normal pelo fator correspondente. Para hora extra de 50%, multiplique por 1,50. Para 100%, multiplique por 2,00. A fórmula é: <strong>Hora normal × Fator = Valor da hora extra</strong>.
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-dark-text mb-2">3. Multiplique pela quantidade de horas extras</h3>
              <p className="text-base text-body-text">
                Pegue o valor da hora extra e multiplique pela quantidade de horas trabalhadas além da jornada. O resultado é o valor total que deve ser pago. Fórmula: <strong>Hora extra × Quantidade = Total a receber</strong>.
              </p>
            </div>
          </div>

          <div className="mt-8 bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-dark-text mb-4">Exemplo prático — Hora extra de 50%:</h3>
            <p className="text-base text-body-text mb-3">
              Salário: R$ 3.000,00 | Jornada: 44h semanais | Horas extras: 15 horas
            </p>
            <p className="text-base text-body-text mb-2">Hora normal: R$ 3.000 ÷ 220 = <strong>R$ 13,64</strong></p>
            <p className="text-base text-body-text mb-2">Hora extra 50%: R$ 13,64 × 1,50 = <strong>R$ 20,45</strong></p>
            <p className="text-base text-body-text font-semibold">Total: R$ 20,45 × 15 = <strong className="text-blue-primary">R$ 306,82</strong></p>
          </div>

          <div className="mt-6 bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-dark-text mb-4">Exemplo prático — Hora extra de 100% (domingo):</h3>
            <p className="text-base text-body-text mb-3">
              Salário: R$ 3.000,00 | Jornada: 44h semanais | Horas extras: 8 horas
            </p>
            <p className="text-base text-body-text mb-2">Hora normal: R$ 3.000 ÷ 220 = <strong>R$ 13,64</strong></p>
            <p className="text-base text-body-text mb-2">Hora extra 100%: R$ 13,64 × 2,00 = <strong>R$ 27,27</strong></p>
            <p className="text-base text-body-text font-semibold">Total: R$ 27,27 × 8 = <strong className="text-blue-primary">R$ 218,18</strong></p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Hora Extra Noturna — Como Calcular
          </h2>
          <p className="text-base text-body-text mb-4">
            Quando o trabalhador faz horas extras no período noturno (entre 22h e 5h), ele tem direito a dois acréscimos que se acumulam: o <strong>adicional de hora extra</strong> e o <strong>adicional noturno de 20%</strong>.
          </p>
          <p className="text-base text-body-text mb-4">
            Além disso, a hora noturna é reduzida: cada hora corresponde a 52 minutos e 30 segundos. Isso significa que 7 horas noturnas equivalem a 8 horas diurnas para fins de remuneração.
          </p>
          
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-dark-text mb-4">Exemplo — Hora extra noturna:</h3>
            <p className="text-base text-body-text mb-2">Salário: R$ 2.500,00 | Divisor: 220</p>
            <p className="text-base text-body-text mb-2">Hora normal: R$ 2.500 ÷ 220 = R$ 11,36</p>
            <p className="text-base text-body-text mb-2">Hora normal + adicional noturno 20%: R$ 11,36 × 1,20 = R$ 13,64</p>
            <p className="text-base text-body-text font-semibold">Hora extra noturna 50%: R$ 13,64 × 1,50 = <strong className="text-blue-primary">R$ 20,45</strong></p>
          </div>

          <p className="text-base text-body-text mt-4">
            Para saber mais sobre o cálculo do adicional noturno isoladamente, veja nossa <Link to="/calculadora-de-adicional-noturno" className="text-blue-primary hover:underline font-semibold">calculadora de adicional noturno</Link>.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            O Que a CLT Diz Sobre Horas Extraordinárias
          </h2>
          <p className="text-base text-body-text mb-4">
            A legislação trabalhista brasileira estabelece regras claras sobre as horas extraordinárias. Conhecer esses limites protege tanto o trabalhador quanto o empregador.
          </p>
          <p className="text-base text-body-text mb-4">
            O artigo 59 da CLT permite que a jornada seja acrescida de no máximo <strong>2 horas extras por dia</strong>, mediante acordo individual, convenção coletiva ou acordo coletivo de trabalho.
          </p>
          <p className="text-base text-body-text mb-4">
            Com a Reforma Trabalhista de 2017, passou a ser possível a adoção do <strong>banco de horas</strong> por acordo individual, desde que a compensação ocorra dentro de 6 meses. Quando estabelecido por acordo coletivo, o prazo é de 1 ano.
          </p>
          <p className="text-base text-body-text">
            <strong>Atenção:</strong> Horas extras habituais integram o cálculo de férias, 13º salário, FGTS e aviso prévio. Se você faz horas extras com frequência, esses valores devem ser considerados na sua remuneração total.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Banco de Horas ou Horas Extras Pagas?
          </h2>
          <p className="text-base text-body-text mb-4">
            Muitas empresas adotam o sistema de <strong>banco de horas</strong> como alternativa ao pagamento de horas extras. Nesse modelo, as horas excedentes são compensadas com folgas futuras, em vez de serem pagas em dinheiro.
          </p>
          <p className="text-base text-body-text mb-4">
            A principal diferença é financeira: no banco de horas, a compensação é feita hora por hora (1 hora extra = 1 hora de folga), sem o acréscimo de 50% ou 100%. Já no pagamento direto, o trabalhador recebe o valor integral com todos os adicionais.
          </p>
          <p className="text-base text-body-text mb-4">
            Para o trabalhador, o pagamento direto costuma ser mais vantajoso financeiramente. Para a empresa, o banco de horas reduz custos com folha de pagamento. A escolha depende do acordo entre as partes e da convenção coletiva da categoria.
          </p>
          <p className="text-base text-body-text">
            Saiba mais sobre como funciona o banco de horas na nossa página sobre <Link to="/calculadora-de-banco-de-horas" className="text-blue-primary hover:underline font-semibold">banco de horas</Link>.
          </p>
        </section>

        <FAQAccordion faqs={faqs} />

        <RelatedTools 
          tools={[
            { 
              title: 'Calculadora de Horas',
              description: 'Somar e subtrair horas de trabalho',
              link: '/'
            },
            { 
              title: 'Calculadora de Horas Extras',
              description: 'Calcule hora extra 50% e 100%',
              link: '/calculadora-de-horas-extras'
            },
            { 
              title: 'Adicional Noturno',
              description: 'Calcule o valor do adicional noturno',
              link: '/calculadora-de-adicional-noturno'
            },
            { 
              title: 'Calculadora de Tempo',
              description: 'Somar, subtrair e converter tempo',
              link: '/calculadora-de-tempo'
            }
          ]}
        />
      </main>

      <Footer />
    </>
  );
};

export default CalculoHorasExtraordinariasPage;
